<template>
  <main class="paymentChoices">
    <div class="paymentChoices__message">
      <div class="message__icon icon-circle">
        <InsuranceCardSvg class="message__icon--insurance" />
      </div>

      <div class="message__text">
        <h1 class="text__title">Hoje, você vai usar convênio no seu atendimento?</h1>
        <p class="text__info">
          Cadastrando seu convênio agora pelo Pré Atendimento, fica mais fácil acessá-lo nas
          próximas consultas.
        </p>
      </div>
    </div>

    <div class="button__group">
      <VButton
        id="paymentChoices-insurance-btn"
        text="Vou usar convênio"
        @click.native="checkPreviousPayment()"
      />
      <VButton
        id="paymentChoices-private-btn"
        text="Não vou usar convênio"
        variant="secondary"
        @click.native.once="choosePrivatePayment"
      />
    </div>
  </main>
</template>

<script>
import {
  getHoursDifferenceMixin,
  routeHelperMixin,
  dataLayersMixin,
  admissionProcessFinishedMixin,
  updatePaymentToPrivate,
} from '@/utils/mixins';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { PRIVATE_PAYMENT_BASE } from '@/utils/constants/payment.constants';
import { handlePaymentMixin } from '@/utils/mixins/handlePayment.mixin';

export default {
  name: 'PaymentChoices',
  mixins: [
    getHoursDifferenceMixin,
    routeHelperMixin,
    dataLayersMixin,
    admissionProcessFinishedMixin,
    updatePaymentToPrivate,
    handlePaymentMixin,
  ],
  data: () => ({
    nextStep: {
      name: 'change-insurance',
      params: {
        path: 'cliente',
        header: {
          hidePreviousBtn: false,
          title: 'Escolha seu convênio',
        },
      },
    },
  }),
  computed: {
    ...mapState({
      exams: ({ currentAdmission }) => currentAdmission.data.medicalOrders[0].exams,
      admission: ({ currentAdmission }) => currentAdmission.data,
      manuallySelectedExams: ({ searchExams }) => searchExams.data.manuallySelectedExams,
    }),
    ...mapGetters({
      haveScheduledExams: 'haveScheduledExams',
      isT2FlowAllowed: 'global/isT2FlowAllowed',
      isFlowStartedWithPaymentChoice: 'global/isFlowStartedWithPaymentChoice',
      isOnlinePaymentEnabled: 'global/isOnlinePaymentEnabled',
    }),
  },
  async created() {
    this.mostCommonPagesAnalytics({
      eventType: 'funnel_step',
      currentStep: 'Vai usar convênio no seu atendimento?',
    });
    await this.getPaymentByAdmissionId();
  },
  methods: {
    ...mapActions({
      checkAndSetT2Flow: 'checkAndSetT2Flow',
      updateFlowSteps: 'global/updateFlowSteps',
      setAdmissionNotElegible: 'setAdmissionNotElegible',
      getLatestHealthInsuranceCards: 'getLatestHealthInsuranceCards',
      getPaymentByAdmissionId: 'getPaymentByAdmissionId',
    }),
    ...mapMutations({
      enableLoader: 'enableLoader',
    }),
    async checkPreviousPayment() {
      try {
        this.enableLoader(true);
        await this.setAdmissionNotElegible();

        if (this.admission.source.origin === 'AUT' && !this.admission.payment) {
          const healthInsuranceCards = await this.getLatestHealthInsuranceCards();

          if (healthInsuranceCards && healthInsuranceCards.length) {
            await this.getPaymentByAdmissionId();
            this.enableLoader(false);
            return this.goNextStep('insurance_card_step');
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.enableLoader(false);
      }

      if (this.admission.payments && this.admission.payments[0].details?.negotiationVariables) {
        this.goNextStep('insurance_card_step');
      } else {
        this.goToInsuranceOptions();
      }
    },

    async goToInsuranceOptions() {
      const routerLinkParams = {
        name: 'change-insurance',
        params: {
          path: 'cliente',
          header: {
            hidePreviousBtn: false,
            title: 'Escolha seu convênio',
          },
        },
      };

      return this.$router.push(routerLinkParams);
    },

    async choosePrivatePayment() {
      if (this.isFlowStartedWithPaymentChoice) {
        await this.handlePayment_mixin(PRIVATE_PAYMENT_BASE);

        this.updateFlowSteps([5, 2]);
        return this.goNextStep('amount_exams');
      }

      await this.checkAndSetT2Flow();
      this.updateFlowSteps([5, 2]);

      if (this.isT2FlowAllowed || this.isOnlinePaymentEnabled) {
        return this.goNextStep('payment_pre_loading');
      }

      return this.goNextStep('end_flow_success');
    },
    async privatePaymentOption() {
      await this.handlePayment_mixin(PRIVATE_PAYMENT_BASE);

      this['global/updateFlowSteps']([5, 2]);

      await this.checkAndSetT2Flow();

      if (this.isScheduledAdmission) {
        const hoursDifference = await this.returnHoursDifference(new Date(), this.examDate);
        if (this.isT2FlowAllowed && hoursDifference > 2) {
          this.goNextStep('queue_end_flow');
        } else if (this.isT2FlowAllowed && hoursDifference <= 2) {
          this.goNextStep('end_flow_success');
        } else {
          this.goNextStep('admission_done');
        }
      } else {
        this.goNextStep('end_flow_success');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.paymentChoices {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &__message {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-content: center;
    align-items: center;
    max-width: 80%;

    .message {
      &__icon {
        &--insurance {
          fill: var(--color-primary);
          height: 48px;
        }
      }

      &__text {
        .text__title {
          color: #000000;
          font-weight: bold;
          padding: 24px 0 12px 0;
        }
      }
    }
  }

  .button__group {
    width: 100%;
    button {
      margin-bottom: 16px;
      background-color: var(--color-primary);
      border-color: var(--color-primary);
      color: #fff;

      &:nth-child(2) {
        background-color: transparent;
        color: var(--color-primary);
        border-color: transparent;
      }
    }
  }
}
</style>
